/** Single-segment SPA / system names that must never be short-link hashes. */
export const RESERVED_HASHES = new Set([
	"api",
	"assets",
	"login",
	"new",
	"links",
	"settings",
	"reset",
	"logout",
	"me",
	"health",
	"admin",
	"bookmarklet",
	"blocks",
	"logs",
	"ips",
]);

export const HASH_ALPHABET =
	"ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

export const HASH_MIN_LEN = 3;
export const HASH_MAX_LEN = 32;
export const HASH_AUTO_START_LEN = 5;

const HASH_RE = /^[A-Za-z0-9]{3,32}$/;

export function isReservedHash(hash: string): boolean {
	return RESERVED_HASHES.has(hash.toLowerCase());
}

export function isValidHashFormat(hash: string): boolean {
	return HASH_RE.test(hash);
}

export type HashValidationError = "FORMAT" | "RESERVED";

/** Returns null if valid, else a machine reason. */
export function validateHash(hash: string): HashValidationError | null {
	if (!isValidHashFormat(hash)) return "FORMAT";
	if (isReservedHash(hash)) return "RESERVED";
	return null;
}

/** Root-router path segment match: /^\/[A-Za-z0-9]{3,32}$/ without leading slash. */
export function isRedirectableHash(segment: string): boolean {
	return isValidHashFormat(segment) && !isReservedHash(segment);
}

/**
 * Generate a random hash of the given length from the URL-safe alphabet.
 * Never returns a reserved name (retries until free of `RESERVED_HASHES`).
 *
 * @throws {RangeError} if `length` is outside {@link HASH_MIN_LEN}–{@link HASH_MAX_LEN}
 * @throws {Error} if 64 attempts all hit reserved names — probability is
 *   effectively zero for length ≥ 5 (≈ (11/62^n)^64). Callers in UI event
 *   handlers should still catch or use a try/fallback if they cannot surface
 *   an error boundary.
 */
export function generateHash(length = HASH_AUTO_START_LEN): string {
	if (length < HASH_MIN_LEN || length > HASH_MAX_LEN) {
		throw new RangeError(`hash length must be ${HASH_MIN_LEN}–${HASH_MAX_LEN}`);
	}
	for (let attempt = 0; attempt < 64; attempt++) {
		const bytes = new Uint8Array(length);
		crypto.getRandomValues(bytes);
		let out = "";
		for (let i = 0; i < length; i++) {
			out += HASH_ALPHABET[bytes[i]! % HASH_ALPHABET.length]!;
		}
		if (!isReservedHash(out)) return out;
	}
	throw new Error("Unable to generate non-reserved hash");
}
